import React, { useEffect } from 'react';
import 'react-tabs/style/react-tabs.css';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { isIBAN } from 'validator';
import { updateAuthUser } from '../../../actions/auth';
import { Spinner, Alert } from '../../../services/notiflix.service';
import AccountSettingsService from '../../../services/account-settings.service';
import { useSelector, useDispatch } from 'react-redux';
import UserProfileDetails from './UserProfile';
import { CloseButtonSvgIcon } from '../IconProvider';
import { useTranslation } from 'react-i18next';

const CompleteProfileModal = ({
  modelClose,
  userProfile,
  setUserProfile,
  showPopup,
}) => {
  const dispatch = useDispatch();
  const { user: _authUser } = useSelector((state) => state.auth);
  const { t } = useTranslation(['profile', 'common', 'auth']);
  useEffect(() => {
    getProfileDetails();
  }, []);
  let validationShape = {
    email: Yup.string()
      .required(t('auth:email_required'))
      .email(t('auth:email_invalid')),
    full_name: Yup.string()
      .required(t('auth:full_name_required'))
      .matches(/^[^\s]+(\s[^\s]+)+$/, t('auth:name_format_required'))
      .max(40),

    phone_number: Yup.string().test(
      'is-valid-phone-number',
      t('auth:phone_number_invalid'),
      (value) => !value || /^\+?\d+$/.test(value),
    ),
    countryId: Yup.number().required(t('country_required')).nullable(),
  };

  //legal_entity_identifier validation
  validationShape['legal_entity_identifier'] = Yup.lazy(() =>
    Yup.string().when('legal_entity_identifier', {
      is: (legal_entity_identifier) => legal_entity_identifier,
      then: () =>
        Yup.string()
          .min(20, t('auth:legal_entity_id_length'))
          .max(20, t('auth:legal_entity_id_invalid_length')),
    }),
  );
  //business name validation
  validationShape['business_name'] = Yup.lazy(() => {
    if (isBusiness) {
      return Yup.string().required(t('auth:business_name_required'));
    } else {
      return Yup.string()
        .transform(() => '')
        .nullable();
    }
  });

  //Commerce name validation
  validationShape['commerce_number'] = Yup.lazy(() => {
    if (isBusiness) {
      return Yup.string()
        .required(t('auth:commerce_number_required'))
        .min(2, t('auth:commerce_number_min_digits'))
        .max(100, t('auth:commerce_number_max_digits'))
        .matches(/^\d+$/, t('auth:commerce_number_invalid'));
    } else {
      return Yup.number()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value,
        )
        .nullable();
    }
  });

  //postal code validation
  validationShape['postal_code'] = Yup.lazy(() =>
    Yup.string().when(['address'], {
      is: (address) => address,
      then: () => Yup.string().required(t('auth:postal_code_required')),
    }),
  );
  // city validation
  validationShape['city'] = Yup.lazy(() =>
    Yup.string()

      .when(['address'], {
        is: (address) => address,
        then: () =>
          Yup.string()
            .required(t('auth:city_required'))
            .max(30, t('auth:city_max_length')),
      }),
  );

  //address validation
  validationShape['address'] = Yup.lazy(() =>
    Yup.string()

      .max(100, t('auth:address_max_length'))
      .when(['city'], {
        is: (city) => city,
        then: () => Yup.string().required(t('auth:address_required')),
      }),
  );

  //account number validation
  validationShape['account_no'] = Yup.lazy(() =>
    Yup.string()
      .required(t('auth:account_number_required'))
      .matches(/^[A-Z0-9]*$/, t('auth:account_number_format'))
      .test('IBAN', t('auth:iban_invalid'), (val) => {
        if (val) {
          return isIBAN(val);
        }
        return true;
      }),
  );

  // account holder name validation
  validationShape['account_holder_name'] = Yup.lazy(() =>
    Yup.string()
      .required(t('auth:account_holder_name_required'))
      .matches(/^[A-Za-z ]*$/, t('auth:valid_account_holder_name_required'))
      .min(3, t('auth:account_holder_name_min_length'))
      .max(40, t('auth:account_holder_name_max_length')),
  );

  const _validationSchema = Yup.object().shape(validationShape);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(_validationSchema),
  });

  const isBusiness = watch('isBusiness');

  const getProfileDetails = async () => {
    try {
      let resp = await AccountSettingsService.details();
      Spinner.hide();
      if (resp.status === 'failure') {
        Alert.error(resp.message);
        return false;
      }
      setUserProfile(resp?.data);
      const savedValues = {
        full_name: resp?.data.full_name ? resp.data.full_name : '',
        email: resp.data?.email ? resp.data.email : '',
        address: resp.data?.address ? resp.data.address : '',
        isBusiness: resp.data?.is_business ? resp.data?.is_business : 0,
        business_name: resp.data?.business_name
          ? resp?.data?.business_name
          : '',
        commerce_number: resp.data?.commerce_number
          ? resp?.data?.commerce_number
          : '',
        phone_number: resp.data?.phone_number ? resp.data?.phone_number : '',
        legal_entity_identifier: resp.data?.legal_entity_identifier
          ? resp.data?.legal_entity_identifier
          : '',
        postal_code: resp.data?.postal_code ? resp.data.postal_code : '',
        city: resp.data?.city ? resp.data.city : '',
        countryId: resp.data?.country_id,
        account_holder_name: resp.data?.userBankDetail?.account_holder_name
          ? resp.data?.userBankDetail?.account_holder_name
          : '',
        account_no: resp.data?.userBankDetail?.account_no
          ? resp.data?.userBankDetail?.account_no
          : '',
      };

      reset(savedValues);
      let authUser = { ..._authUser };
      authUser['profilePicture'] = resp.data.profilePicture;
      authUser['userName'] = resp.data.full_name;
      authUser['bank_details'] = resp.data?.userBankDetail?.account_no
        ? true
        : false;
      authUser['kyc_status'] = resp.data?.kyc_status;
      authUser['platform_agreement_status'] =
        resp.data?.platformAgreements?.length > 0
          ? resp.data?.platformAgreemets[0]?.agreement_status
          : 0;
      dispatch(updateAuthUser(authUser));
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
    }
  };
  const updateProfile = async (_input) => {
    try {
      Spinner.show();
      let formData = new window.FormData();

      formData.append('full_name', _input.full_name);
      formData.append('email', _input.email);
      if (_input.countryId) {
        formData.append('country_id', _input.countryId);
      }
      formData.append('is_business', _input?.isBusiness ? 1 : 0);
      if (_input?.isBusiness) {
        if (_input.business_name) {
          formData.append('business_name', _input.business_name);
        }
        if (_input.commerce_number) {
          formData.append('commerce_number', _input.commerce_number);
        }
        if (_input.legal_entity_identifier) {
          formData.append(
            'legal_entity_identifier',
            _input.legal_entity_identifier,
          );
        }
      } else {
        formData.append('business_name', '');
        formData.append('commerce_number', '');
        formData.append('legal_entity_identifier', '');
      }
      if (_input.phone_number) {
        formData.append('phone_number', _input.phone_number);
      }
      if (_input.address) {
        formData.append('address', _input.address || '');
      }
      if (_input.account_no) {
        formData.append('account_no', _input.account_no);
      }
      if (_input.postal_code) {
        formData.append('postal_code', _input.postal_code || '');
      }
      if (_input.city) {
        formData.append('city', _input.city);
      }
      if (_input.account_holder_name) {
        formData.append('account_holder_name', _input.account_holder_name);
      }

      let resp = await AccountSettingsService.update(formData);

      if (resp.status === 'failure') {
        Spinner.hide();
        Alert.error(resp.message);
        return false;
      }
      await getProfileDetails();
      Spinner.hide();
      Alert.success(t('auth:profile_updated_successfully'));
      modelClose();
      if (showPopup) {
        showPopup();
      }
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
      return false;
    }
  };

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto mx-3 my-4 max-w-[565px] ">
          {/*content*/}
          <form onSubmit={handleSubmit(updateProfile)}>
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full dark:text-white dark:bg-[#2f3f56]    bg-white outline-none focus:outline-none md:max-w-[565px] md:min-w-[565px] md:min-h-[470px] p-6">
              <button
                type="button"
                className="p-1 ml-auto bg-transparent border-0 text-black text-3xl leading-none font-semibold outline-none focus:outline-none absolute right-1 top-3 z-10 flex justify-center items-center"
                onClick={modelClose}
              >
                <span className="bg-transparent text-black dark:text-white w-10 dark:bg-slate-400 dark:rounded-md block outline-none focus:outline-none">
                  <CloseButtonSvgIcon />
                </span>
              </button>
              <UserProfileDetails
                setValue={setValue}
                register={register}
                clearErrors={clearErrors}
                errors={errors}
                userProfile={userProfile}
                isBusiness={isBusiness}
                showMailField={false}
                countryId={watch('countryId')}
              />

              <div>
                <h5 className="border-b border-[#ccc] mb-2 mt-6 pb-2">
                  {t('common:bank_details')}
                </h5>
                <div className="mb-6 ">
                  <p className="text-md text-[#85919F]">{t('bank_account')}</p>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div className="">
                    <div className="mb-6">
                      <p className="responsive-text">
                        {t('account_number_iban')}
                        <span className="common-error-text">*</span>
                      </p>
                      <input
                        placeholder={t('account_number')}
                        type="text"
                        name="account_no"
                        className="dark:bg-[#4c5972] dark:text-white"
                        {...register('account_no', { required: true })}
                      />
                      <div className="common-error-text">
                        {errors.account_no?.message}
                      </div>
                    </div>
                  </div>
                  <div className="">
                    <div className="mb-6">
                      <p className="responsive-text">
                        {t('common:in_the_name_of')}
                        <span className="common-error-text">*</span>
                      </p>
                      <input
                        placeholder="Peter Smith"
                        type="text"
                        name="account_holder_name"
                        className="dark:bg-[#4c5972] dark:text-white"
                        {...register('account_holder_name', { required: true })}
                      />
                      <div className="common-error-text">
                        {errors.account_holder_name?.message}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 ">
                <div className="flex justify-end items-end md:mb-0 mb-4">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="bg-[#AAAAAA] text-white"
                      onClick={modelClose}
                    >
                      {t('common:cancel')}
                    </button>
                    <button type="submit" className="bg-GreenColor text-black">
                      {t('save_and_continue')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </>
  );
};
export default CompleteProfileModal;
